import React, { useState, useEffect } from 'react'
import { CircularProgress } from '@mui/material'
import StyledButton from '@/components/Button/Button'
import FormInput from '@/components/Common/FormInput'
import Popup from '@/components/Common/Popup'
import Loader from '@/components/Loader'
import useGetProfile from '../services/hooks/useGetProfile'
import useUpdateProfile from '../services/hooks/usePutUpdateProfile'
import cacheKeys from '@/services/cacheKeys'
import useValidation from '@/hooks/useValidation'
import { invalidateQuery } from '@/services/config/queryClient'
import { formatPhoneNumber } from '@/utils'

const Profile: React.FC = () => {
  const inputValidationRules = {
    firstName: [
      { required: true, message: 'Please enter your first name.' },
      {
        pattern: /^[a-zA-Z\s]+$/,
        message: 'First name must contain letters only.',
      },
      {
        pattern: (value: string) => value.length <= 20,
        message: 'First name cannot exceed 20 characters.',
      },
    ],
    lastName: [
      { required: true, message: 'Please enter your last name.' },
      {
        pattern: /^[a-zA-Z\s]+$/,
        message: 'Last name must contain letters only.',
      },
      {
        pattern: (value: string) => value.length <= 20,
        message: 'Last name cannot exceed 20 characters.',
      },
    ],
  }

  const [errorMsg, setErrorMsg] = useState<string>('')
  const [firstName, setFirstName] = useState<string>('')
  const [lastName, setLastName] = useState<string>('')
  const [showPopup, setShowPopup] = useState<boolean>(false)
  const { errors, validateInputs } = useValidation(
    {
      firstName,
      lastName,
    },
    inputValidationRules,
  )

  const { data: getProfile, isLoading } = useGetProfile()
  const { mutate: updateProfile, isPending, isError } = useUpdateProfile()

  const profileData = getProfile?.data
  const userProfile = profileData?.userProfile

  const isProfileUpdate =
    firstName !== userProfile?.firstName || lastName !== userProfile?.lastName

  const handleClickDone = () => {
    setShowPopup(false)
  }

  const handleChangeFirstName = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setFirstName(event.target.value)
  }

  const handleChangeLastName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLastName(event.target.value)
  }

  const handleSave = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!validateInputs()) {
      return
    }
    if (firstName && lastName && isProfileUpdate) {
      const requestData = {
        firstName,
        lastName,
        phone: userProfile?.phone || '',
      }

      const formData = new FormData()
      formData.append('firstName', requestData.firstName)
      formData.append('lastName', requestData.lastName)
      formData.append('phone', requestData.phone.toString())

      updateProfile(formData, {
        onSuccess: () => {
          setShowPopup(true)
          invalidateQuery([cacheKeys.GET_PROFILE])
        },
        onError: (error) => {
          setErrorMsg(
            error.message || 'Something went wrong. Please try again.',
          )
        },
      })
    }
  }

  useEffect(() => {
    if (userProfile) {
      setFirstName(userProfile.firstName || '')
      setLastName(userProfile.lastName || '')
    }
  }, [profileData])

  if (isLoading) {
    return <Loader />
  }

  return (
    <>
      <form onSubmit={handleSave}>
        <div className="mt-6 flex flex-col gap-1 w-full md:w-2/3">
          <div className="flex flex-col gap-4">
            <FormInput
              type="text"
              label="First Name"
              value={firstName}
              onChange={handleChangeFirstName}
              error={!!errors.firstName}
              helperText={errors.firstName}
            />
            <FormInput
              type="text"
              label="Last Name"
              value={lastName}
              onChange={handleChangeLastName}
              error={!!errors.lastName}
              helperText={errors.lastName}
            />
            <FormInput
              type="text"
              label="Phone Number"
              value={formatPhoneNumber(
                userProfile?.phone || '',
                userProfile?.countryCode || '',
              )}
              isDisabled={true}
            />
            <FormInput
              type="email"
              label="Email Address"
              value={userProfile?.userId?.email || ''}
              isDisabled={true}
            />
          </div>
          <div className="h-6 text-left">
            {isError && <p className="text-red-600 text-sm">{errorMsg}</p>}
          </div>
          <StyledButton
            className="h-14 w-52"
            type="submit"
            disabled={
              firstName === '' ||
              lastName === '' ||
              !isProfileUpdate ||
              isPending
            }
          >
            {isPending ? (
              <CircularProgress className="!text-[#181a21] !text-2xl" />
            ) : (
              'Save Changes'
            )}
          </StyledButton>
        </div>
      </form>
      {showPopup && (
        <Popup
          title="Profile Reset Successfully"
          message="Your profile changes have been saved, and your account is up to date."
          buttonText="Done"
          onButtonClick={handleClickDone}
        />
      )}
    </>
  )
}

export default Profile
